import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { services } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactFaqs = [
  { q: "How quickly will I get a response?", a: "Every consultation request is reviewed by a partner and acknowledged within one business day. For urgent matters (interim relief, criminal complaints, time-bound notices), call directly on +91 96435 72870 or WhatsApp +91 96435 72870 — we triage urgent matters the same day." },
  { q: "Do I need to bring documents to the first meeting?", a: "Where available, please bring: relevant agreements/notices, copies of FIRs or court summons, ID proof, and a one-page chronology of events. The diagnostic conference is more productive when both sides have the documentary record in hand." },
  { q: "What is the cost of an initial consultation?", a: "Initial consultations are scoped during the call. Fees vary by matter complexity. We provide a written engagement letter with milestone-linked or capped retainer fees before any substantive work begins." },
  { q: "Can I have the consultation virtually?", a: "Yes. We conduct first consultations over Zoom, Google Meet, WhatsApp video or telephone — particularly for NRI clients and out-of-Delhi engagements. Document review is handled through encrypted file transfer." },
  { q: "How is client confidentiality protected?", a: "All engagements are governed by professional privilege under the Bar Council of India Rules. We use secure document portals, NDAs where appropriate, and controlled internal access — particularly for sensitive matrimonial, M&A and investigation matters." },
  { q: "Do you handle pro-bono or government-assigned matters?", a: "We accept select pro-bono matters of constitutional or public-interest character on a discretionary basis. For government-assigned panel work, please reach out via the contact form." },
];

const contactSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LegalService",
      name: "K & K Associates - Advocates & Solicitors",
      url: "https://kandkassociates.com/contact",
      telephone: "+91-96435-72870",
      email: "info@kandkassociates.in",
      address: {
        "@type": "PostalAddress",
        streetAddress: "A-197, LGF, Defence Colony",
        addressLocality: "New Delhi",
        postalCode: "110024",
        addressRegion: "Delhi",
        addressCountry: "IN",
      },
      geo: { "@type": "GeoCoordinates", latitude: 28.5722, longitude: 77.2305 },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+91-96435-72870",
        contactType: "customer service",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi", "Punjabi"],
      },
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:30",
        closes: "19:00",
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: contactFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://kandkassociates.com/" },
        { "@type": "ListItem", position: 2, name: "Contact" },
      ],
    },
  ],
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", subject: "", message: "", website: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: typeof e === "string" ? e : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — our team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", subject: "", message: "", website: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact K & K Associates"
        description="Contact K & K Associates, Advocates & Solicitors at A-197, LGF, Defence Colony, New Delhi 110024. Call +91 96435 72870, email info@kandkassociates.in or WhatsApp +91 96435 72870."
        canonical="https://kandkassociates.com/contact"
        keywords={["contact K and K Associates", "law firm Defence Colony contact", "advocates Delhi contact"]}
        schema={contactSchema}
      />

      <section className="pt-28 md:pt-40 pb-12 md:pb-16 bg-paper border-b border-rule">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-6">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[1] sm:leading-[0.98] text-ink max-w-4xl">
            Tell us your matter.<br/>
            <span className="italic text-brand">We will tell you the path forward.</span>
          </h1>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 bg-paper p-8 md:p-12 border border-rule" data-testid="contact-form">
            <p className="kicker mb-3">Consultation Request</p>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">Begin a private conversation.</h2>
            <p className="mt-4 font-serif text-lg text-ink/70">All enquiries are reviewed by a partner. We respond within one business day.</p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={update("website")}
                className="hidden"
                data-testid="contact-website"
              />
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Full Name *</label>
                <Input value={form.name} onChange={update("name")} placeholder="Your full name" data-testid="contact-name" className="rounded-none border-rule h-12" required />
              </div>
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Phone *</label>
                <Input value={form.phone} onChange={update("phone")} placeholder="+91 ..." data-testid="contact-phone" className="rounded-none border-rule h-12" required />
              </div>
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Email *</label>
                <Input type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="contact-email" className="rounded-none border-rule h-12" required />
              </div>
              <div className="md:col-span-1">
                <label className="kicker block mb-2">Practice Area</label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger className="rounded-none border-rule h-12" data-testid="contact-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>{s.title.split("—")[0].trim()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="kicker block mb-2">Subject</label>
                <Input value={form.subject} onChange={update("subject")} placeholder="Brief subject" data-testid="contact-subject" className="rounded-none border-rule h-12" />
              </div>
              <div className="md:col-span-2">
                <label className="kicker block mb-2">Message *</label>
                <Textarea value={form.message} onChange={update("message")} placeholder="Tell us about your matter — please avoid sharing privileged or sensitive details until we engage." data-testid="contact-message" className="rounded-none border-rule min-h-[140px]" required />
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn-primary mt-8 w-full md:w-auto" data-testid="contact-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
              {submitting ? "Sending..." : "Submit Enquiry"}
            </button>

            <p className="mt-5 text-xs text-ink/50 font-sans tracking-wide">
              Submitting this form does not create a lawyer-client relationship.
            </p>
          </form>

          {/* Contact info */}
          <aside className="lg:col-span-5 space-y-8">
            <div className="bg-brand text-white p-8 md:p-10">
              <p className="kicker text-white/70 mb-5">Office</p>
              <h3 className="font-display text-2xl md:text-3xl leading-tight">K &amp; K Associates</h3>
              <p className="font-serif text-lg text-white/85 mt-2">Advocates &amp; Solicitors</p>

              <div className="mt-8 space-y-5 font-serif text-base">
                <div className="flex gap-4">
                  <MapPin className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} />
                  <p data-testid="contact-info-address">A-197, LGF, Defence Colony,<br/>New Delhi – 110024</p>
                </div>
                <div className="flex gap-4">
                  <Phone className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} />
                  <a href="tel:919643572870" className="link-underline" data-testid="contact-info-phone">+91 96435 72870, 05, 06</a>
                </div>
                <div className="flex gap-4">
                  <Mail className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} />
                  <a href="mailto:info@kandkassociates.in" className="link-underline" data-testid="contact-info-email">info@kandkassociates.in</a>
                </div>
                <div className="flex gap-4">
                  <MessageCircle className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} />
                  <a href="https://wa.me/919643572870" target="_blank" rel="noreferrer" className="link-underline" data-testid="contact-info-whatsapp">+91 96435 72870 (WhatsApp)</a>
                </div>
              </div>
            </div>

            <div className="border border-rule overflow-hidden">
              <iframe
                title="K & K Associates location"
                src="https://www.google.com/maps?q=Defence+Colony+A-197+New+Delhi&output=embed"
                width="100%"
                height="280"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>

            <div className="bg-paper border border-rule p-8">
              <p className="kicker mb-4">Office Hours</p>
              <p className="font-serif text-lg text-ink/85">Monday – Saturday<br/>9:30 AM – 7:00 PM IST</p>
              <p className="mt-3 font-serif text-sm text-ink/60">Sundays &amp; public holidays — by prior appointment.</p>
            </div>
          </aside>
        </div>
      </section>

      {/* Reach & Areas We Serve — GEO content */}
      <section className="py-14 md:py-20 bg-paper border-t border-rule" data-testid="contact-geo-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="kicker mb-5">Geographic Reach</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
                Serving Delhi NCR — and <span className="italic text-brand">all of India.</span>
              </h2>
              <p className="mt-6 font-serif text-lg text-ink/75 leading-relaxed">
                Our office at A-197, Lower Ground Floor, Defence Colony places us in the heart of South Delhi — minutes from Saket District Court Complex, the Indian Patent Office and the Delhi High Court. For matters across India, our vetted local-counsel network ensures coverage without sacrificing the quality of central oversight.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-rule border border-rule">
              {[
                "South Delhi", "Central Delhi", "North Delhi", "East Delhi", "West Delhi", "Lutyens' Delhi",
                "Gurgaon (Gurugram)", "Noida & Greater Noida", "Faridabad", "Ghaziabad", "Sonipat", "Bahadurgarh",
                "Mumbai (via correspondent)", "Bengaluru (via correspondent)", "Chennai (via correspondent)", "Hyderabad", "Kolkata", "Pan-India",
              ].map((c) => (
                <div key={c} className="bg-paper p-5 hover:bg-bone transition-colors">
                  <p className="font-serif text-base text-ink/85">{c}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact FAQ — AEO */}
      <section className="py-14 md:py-20 bg-bone border-t border-rule" data-testid="contact-faq-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-5">Before You Reach Out</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink mb-12">
            Common questions answered.
          </h2>
          <div className="border-t border-rule">
            {contactFaqs.map((f, i) => (
              <details key={f.q} className="group border-b border-rule py-6 cursor-pointer" data-testid={`contact-faq-${i}`}>
                <summary className="flex items-center justify-between gap-6 list-none">
                  <h3 className="font-display text-xl md:text-2xl text-ink leading-snug">{f.q}</h3>
                  <span className="text-brand font-display text-2xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 font-serif text-lg text-ink/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
